import { supertypeClass, property } from '@haventech/amorphic';
import { Everything } from '@hit/amorphic-types';
import { Policy } from './Policy';
import { UwDecisionReasons } from '../static/UwDecisionReasons';
import { RateClasses } from '../static/RateClasses';

@supertypeClass({ toClient: true })
export class UwReasons extends Everything {
  STAGES = {
    ACCELERATED: 'accelerated',
    TLIC: 'tLIC',
    FINAL_ALGO: 'finalAlgo',
    FINAL_MANUAL: 'finalManual',
    FINAL: 'final',
  };

  PRODUCT_SWITCH_RESTRICTION_REASONS = {
    JUVI: 'UnderSeventeenAndHalf',
    LOW_FACE: 'WLWithMaxFaceUnder50k',
    PAY_THE_DIFFERENCE: 'PayTheDifference',
  };

  @property({ getType: () => Policy })
  policy: Policy;

  @property({ toServer: false })
  lastUpdatedTime: Date;

  @property({
    getType: () => [],
    comment: 'Risk groups behind acceleratedRateClass decision',
  })
  acceleratedRateClassReasons: string[] | null = null;

  @property({
    getType: () => [],
    comment: 'Risk groups behind tLICRateClass',
  })
  tLICRateClassReasons: string[] | null = null;

  @property({
    getType: () => [],
    comment: 'Risk groups behind finalAlgoRateClass',
  })
  finalAlgoRateClassReasons: string[] | null = null;

  @property({
    getType: () => [],
    comment: 'Risk groups behind finalManualRateClass',
  })
  finalManualRateClassReasons: string[] | null = null;

  @property({
    comment: 'Underwriter decision comments',
  })
  finalManualRateClassReasonsComment: string | null = null;

  @property({
    getType: () => [],
    comment: 'Risk groups behind finalRateClass',
  })
  finalRateClassReasons: string[] | null = null;

  @property({
    getType: () => [],
    comment: 'Product switch restriction reasons',
  })
  productSwitchRestrictionReasons: string[] | null = null;

  mapToReasonStrings(reasonCodes: string[] | null): string[] | null {
    return !reasonCodes ? null : reasonCodes.map((code) => UwDecisionReasons[code] || code);
  }

  setRateClassReasons(stage: string, reasonCodes: string[] | null) {
    this[`${stage}RateClassReasons`] = this.mapToReasonStrings(reasonCodes);
  }

  setUnderwriterComments(comments: string) {
    this.finalManualRateClassReasonsComment = comments;
  }

  setProductSwitchRestrictionReasons(reasonTypes: string[]) {
    this.productSwitchRestrictionReasons = reasonTypes;
  }

  getDecisionReasonsForDisplay(): { title: string; list: string }[] {
    const reasons = [];
    if (this.policy.finalRateClass === RateClasses.ULTRA.code) {
    } else if (this.policy.finalRateClass > RateClasses.ULTRA.code && this.finalRateClassReasons) {
      // Show if final result is ready and we are not ULTRA rated
      // This includes fluidless Select Preferred rated policies
      reasons.push({
        title: 'Reason(s) for decision:',
        list: this.display('final'),
      });
    } else if (this.policy.applicationSignedDate === null) {
      // Before we go into final underwriting, show why we ended up where we did
      if (this.acceleratedRateClassReasons && this.acceleratedRateClassReasons.length) {
        reasons.push({
          title: "Reason(s) client didn't get a fluidless offer:",
          list: this.display('accelerated'),
        });
      }
      if (!this.policy.isTLIC() && this.tLICRateClassReasons) {
        reasons.push({
          title: "Reason(s) client wasn't offered temporary coverage (TLIC):",
          list: this.display('tLIC'),
        });
      }
    }

    return reasons;
  }

  clear() {
    this.acceleratedRateClassReasons = null;
    this.tLICRateClassReasons = null;
    this.finalAlgoRateClassReasons = null;
    this.finalManualRateClassReasons = null;
    this.finalRateClassReasons = null;
  }

  display(stage: string) {
    const reasons = this[`${stage}RateClassReasons`] || [];
    let ret = '';
    for (let i = 0; i < reasons.length; i++) {
      ret += reasons[i];
      if (i < reasons.length - 1) {
        ret += ', ';
      }
    }

    return ret;
  }

  constructor() {
    super();
  }

  static new(policy) {
    return new UwReasons().init(policy);
  }

  init(policy) {
    this.policy = policy;

    return this;
  }
}
